import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import isMobile from 'ismobilejs';
import { CameraScanInputProps } from '../interface/interface';
import {
  HID_SCANNER_MIN_BARCODE_LENGTH,
  HID_SCANNER_MAX_KEYSTROKE_GAP_MS,
  CAMERA_SCAN_MISS_RESET_THRESHOLD,
} from '../constant/constant';

export default function CameraScanInput({
  onScan,
  disabled = false,
  error,
}: Readonly<CameraScanInputProps>) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const lastScannedCodeRef = useRef<string>('');
  const missStreakRef = useRef(0);
  const hidBufferRef = useRef<{ chars: string; lastKeyTime: number }>({
    chars: '',
    lastKeyTime: 0,
  });
  const isMobileDevice = isMobile(window.navigator).any;
  let statusMessage: string;

  const emitScan = (code: string) => {
    if (code === lastScannedCodeRef.current) return;
    lastScannedCodeRef.current = code;
    onScan(code);
  };

  const startScanning = async () => {
    try {
      const reader = new BrowserMultiFormatReader();
      const controls = await reader.decodeFromConstraints(
        {
          video: { facingMode: 'environment' },
        },
        videoRef.current!,
        (result) => {
          if (!result) {
            missStreakRef.current += 1;
            if (missStreakRef.current >= CAMERA_SCAN_MISS_RESET_THRESHOLD) {
              lastScannedCodeRef.current = '';
            }
            return;
          }
          missStreakRef.current = 0;
          emitScan(result.getText());
        },
      );

      controlsRef.current = controls;
      setIsScanning(true);
      focusHiddenInput();
    } catch (err) {
      console.error('Camera scan error:', err);
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    setIsScanning(false);
    lastScannedCodeRef.current = '';
    missStreakRef.current = 0;
  };

  const handleHiddenInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const now = Date.now();
    const buffer = hidBufferRef.current;
    const gapFromLastKey = now - buffer.lastKeyTime;

    if (e.key === 'Enter') {
      e.preventDefault();
      const code = buffer.chars;
      buffer.chars = '';
      if (hiddenInputRef.current) hiddenInputRef.current.value = '';

      if (
        code.length >= HID_SCANNER_MIN_BARCODE_LENGTH &&
        gapFromLastKey <= HID_SCANNER_MAX_KEYSTROKE_GAP_MS
      ) {
        emitScan(code);
      }
      return;
    }

    if (e.key.length !== 1) return;

    if (buffer.chars && gapFromLastKey > HID_SCANNER_MAX_KEYSTROKE_GAP_MS) {
      buffer.chars = '';
    }

    buffer.chars += e.key;
    buffer.lastKeyTime = now;
  };

  const focusHiddenInput = () => {
    if (!disabled) hiddenInputRef.current?.focus();
  };

  useEffect(() => {
    if (disabled) {
      stopScanning();
      return;
    }
    if (isMobileDevice) {
      startScanning();
    }
    focusHiddenInput();

    return () => {
      controlsRef.current?.stop();
    };
  }, [disabled]);

  if (isScanning) {
    statusMessage =
      'Scanning… point camera at barcode, or use a barcode scanner';
  } else {
    statusMessage = '';
  }

  return (
    <div className="flex flex-col items-center">
      <video
        ref={videoRef}
        className="absolute w-px h-px opacity-0 pointer-events-none -z-10"
        muted
        playsInline
      />

      <input
        ref={hiddenInputRef}
        type="text"
        inputMode="none"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        aria-hidden="true"
        tabIndex={-1}
        disabled={disabled}
        onKeyDown={handleHiddenInputKeyDown}
        onBlur={() => window.setTimeout(focusHiddenInput, 0)}
        className="absolute w-px h-px opacity-0 pointer-events-none -z-10"
      />

      <p className="text-xs text-gray-500 text-center">{statusMessage}</p>
      <p className="text-xs text-red-500 text-center mb-2">{error}</p>
    </div>
  );
}
